import { startCase } from 'lodash';
import { roundingOff } from '../../Components/helper';

const SKIP_TOKENS = ['n/a', 'na', 'n', 'a', '/', '-'];
const EMPTY_PLACEHOLDER = '--';
const BASE_SIZE_MARKER = 'base';

/**
 * True when a stored value is one of the tokens the spec grid accepts in place of a number.
 * Those must survive verbatim — rounding one collapses it to '0' and misreports the row.
 * @param {*} value a stored measurement or grade rule.
 * @returns {boolean}
 */
const isSkipToken = (value) => {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  return SKIP_TOKENS.includes(String(value).toLowerCase().trim());
};

/**
 * Renders a stored measurement for the history column, matching how the spec-pom history
 * rows already round, while letting skip tokens through untouched.
 * @param {*} value a stored measurement.
 * @returns {string}
 */
const formatMeasurement = (value) => {
  if (isSkipToken(value)) {
    return String(value);
  }
  if (value === null || value === undefined || value === '') {
    return EMPTY_PLACEHOLDER;
  }
  return roundingOff(value, 2);
};

/**
 * Renders one size of a base-size history row as `size: measurement (rule)`.
 *
 * The rule is shown because a base-size change is the one operation that leaves every
 * measurement untouched and rewrites every grade rule — a column showing measurements alone
 * would read identically on both sides. An explicit sign is preserved: it encodes the
 * direction of the rule, which the position alone does not always imply.
 * @param {Object} grading a persisted grading row.
 * @returns {string}
 */
export const formatBaseSizeGrading = (grading) => {
  const sizeName = grading?.sizeName || EMPTY_PLACEHOLDER;
  const measurement = formatMeasurement(grading?.measurment);
  if (grading?.isSampleSize) {
    return `${sizeName}: ${measurement} (${BASE_SIZE_MARKER})`;
  }
  const hasRule = grading?.grading === 0 || Boolean(grading?.grading);
  return `${sizeName}: ${measurement} (${hasRule ? String(grading.grading) : EMPTY_PLACEHOLDER})`;
};

/**
 * Renders every size of one POM as a single comma-separated run.
 * @param {Object} pom a persisted pomLibraries / specPomLibraries entry.
 * @returns {string} the run, or '' when the POM carries no gradings.
 */
export const formatBaseSizePomGradings = (pom) =>
  Array.isArray(pom?.gradings) ? pom.gradings.map(formatBaseSizeGrading).join(', ') : '';

/**
 * Reports whether a base-size change actually rewrote this POM.
 *
 * A base-size record stores the whole grading group on both sides so the row stays readable on
 * its own, which means most of what it lists may not have moved at all. Comparing the rendered
 * runs — rather than the raw rows — keeps the impact count and the per-POM marker agreeing with
 * what the column actually shows: a difference the reader cannot see is not an impact.
 * @param {Object|null} lhsPom the POM as it stood before the change.
 * @param {Object|null} rhsPom the POM as it stands after it.
 * @returns {boolean} true when the two sides render differently.
 */
export const hasGradingRunChanged = (lhsPom, rhsPom) => formatBaseSizePomGradings(lhsPom) !== formatBaseSizePomGradings(rhsPom);

const CAD_NOISE_KEYS = ['_id', '__v', 'createdAt', 'updatedAt', 'lastModifiedAt'];
const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;

/**
 * Renders one value of a CAD File snapshot. The snapshot is a copy of a pattern-library
 * document, so a value can be a primitive, a list of sizes or categories, or a nested entity.
 * A bare object id is dropped: it is stored data with no meaning in a history column.
 * @param {*} value a value taken off the stored patternData object.
 * @returns {string} the printable value, or '' when there is nothing worth printing.
 */
const formatCadValue = (value) => {
  if (typeof value === 'boolean' || typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return OBJECT_ID_PATTERN.test(trimmed) ? '' : trimmed;
  }
  if (Array.isArray(value)) {
    return value
      .map(formatCadValue)
      .filter((entry) => entry !== '')
      .join(', ');
  }
  if (value && typeof value === 'object') {
    return formatCadValue(value.name || value.code || value.label || '');
  }
  return '';
};

/**
 * Renders a stored `patternData` snapshot as labelled pairs.
 *
 * `patternData` is an object, not a named entity — the generic object branch of the history
 * row prints `.name`, which a partial diff (the usual history payload) does not carry, so the
 * whole change used to read as '--'. Every key that still holds a value is surfaced instead.
 * @param {Object} patternData the stored CAD File snapshot, whole or a partial diff of it.
 * @returns {Array<{label: string, value: string}>} one pair per meaningful key.
 */
export const formatCadFileFields = (patternData) => {
  if (!patternData || typeof patternData !== 'object' || Array.isArray(patternData)) {
    return [];
  }
  return Object.entries(patternData)
    .filter(([key]) => !CAD_NOISE_KEYS.includes(key))
    .map(([key, value]) => ({ label: startCase(key), value: formatCadValue(value) }))
    .filter((entry) => entry.value !== '');
};

/**
 * Renders a stored `weightVolData` snapshot as one labelled-pair group per size row.
 *
 * `weightVolData` holds a list of `{size, weight, weightUnit, volume, volumeUnit}` rows, while
 * pre-migration documents still hold a single bare object — the history diff hands back
 * whichever shape the change came from, and neither prints through the generic object branch.
 * Both are normalised to rows here and each row reuses the CAD File pair formatter, whose value
 * coercion is exactly what a row needs: nested `{label}` units read by name, bare object ids and
 * audit keys dropped, empty values skipped.
 * @param {Array<Object>|Object} weightVolData the stored Weight/Volume snapshot, whole or a diff.
 * @returns {Array<Array<{label: string, value: string}>>} one pair list per meaningful row.
 */
export const formatWeightVolumeRows = (weightVolData) => {
  const rows = Array.isArray(weightVolData) ? weightVolData : [weightVolData];
  return rows.map(formatCadFileFields).filter((entries) => entries.length > 0);
};

const CODELESS_POM_KEY = 'pom';

/**
 * Keys one side of a Specifications history row so its entries can be matched against the
 * other side. A code identifies a POM; a repeated code is separated by its occurrence, and a
 * row carrying no code at all can only be matched on its position.
 * @param {Array<Object>|*} rows one side of a history field, whatever shape it holds.
 * @returns {Array<{key: string, code: string, row: Object}>} the side, keyed in its own order.
 */
const keyPomRows = (rows) => {
  const seenCounts = new Map();
  return (Array.isArray(rows) ? rows : []).map((row, index) => {
    const code = row?.code ? String(row.code) : '';
    const base = code || `${CODELESS_POM_KEY}-${index}`;
    const occurrence = (seenCounts.get(base) || 0) + 1;
    seenCounts.set(base, occurrence);
    return { key: `${base}#${occurrence}`, code, row };
  });
};

/**
 * Pairs the two sides of a Specifications history row so both columns walk the same POMs.
 *
 * The history differ compacts each side independently and keeps the old side only where an
 * old value existed, so the sides can differ in both length and order — inserting a POM at
 * the front leaves `lhs` one entry shorter, and rendered by position the From column names
 * POM001 against a To column naming POM000, which reads as a rename that never happened.
 * The union of both sides is walked instead, in the new state's order first so the column a
 * reader scans stays in its stored order, with anything only the old state held appended.
 * @param {Array<Object>|*} lhsRows the old side of a history field.
 * @param {Array<Object>|*} rhsRows the new side of a history field.
 * @returns {Array<{key: string, code: string, lhs: (Object|null), rhs: (Object|null)}>} one
 *   entry per POM, in the order both columns must render, absent sides left null.
 */
export const alignPomRowsByCode = (lhsRows, rhsRows) => {
  const keyedLhs = keyPomRows(lhsRows);
  const keyedRhs = keyPomRows(rhsRows);
  const lhsByKey = new Map(keyedLhs.map((entry) => [entry.key, entry]));
  const rhsByKey = new Map(keyedRhs.map((entry) => [entry.key, entry]));
  const orderedKeys = [
    ...keyedRhs.map((entry) => entry.key),
    ...keyedLhs.map((entry) => entry.key).filter((key) => !rhsByKey.has(key)),
  ];
  return orderedKeys.map((key) => {
    const lhsEntry = lhsByKey.get(key);
    const rhsEntry = rhsByKey.get(key);
    return {
      key,
      code: (rhsEntry || lhsEntry)?.code || '',
      lhs: lhsEntry ? lhsEntry.row : null,
      rhs: rhsEntry ? rhsEntry.row : null,
    };
  });
};
